import { useState } from "react";
import { useFetch } from "../lib/useFetch";
import ChartPane, { TFS } from "../components/ChartPane";
import { PageHead, Panel, Tag, Empty } from "../components/common";
import { AIExplain } from "../components/AIExplain";
import { useApp } from "../lib/AppContext";
import { ts } from "../lib/api";

export const SymbolSelect = ({ value, onChange, testid = "symbol-select" }) => {
  const { data: uni } = useFetch("/universe");
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mono w-full min-w-0 max-w-full rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-xs outline-none sm:w-auto sm:max-w-md">
      {(uni || []).map((u) => <option key={u.symbol} value={u.symbol}>{u.symbol} · {u.name} ({u.exchange})</option>)}
    </select>
  );
};

export default function Patterns() {
  const { lang } = useApp();
  const [symbol, setSymbol] = useState("1155.KL");
  const [tf, setTf] = useState("15m");
  const [chart, setChart] = useState(null);
  const { data: uni } = useFetch("/universe");
  const pats = (chart?.patterns || []).slice().sort((a, b) => b.detected_time - a.detected_time);
  return (
    <div data-testid="patterns-page">
      <PageHead title="AI Pattern Analysis" sub="Lifecycle: FORMING → DETECTED → AWAITING CONFIRMATION → CONFIRMED → INVALIDATED / EXPIRED. Detection uses closed bars only; once a pattern reaches a terminal state it is frozen in the database and never repainted."
        right={<div className="flex gap-2"><SymbolSelect value={symbol} onChange={setSymbol} testid="patterns-symbol-select" />
          <select data-testid="patterns-tf-select" value={tf} onChange={(e) => setTf(e.target.value)} className="mono rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 text-xs">{TFS.map((x) => <option key={x}>{x}</option>)}</select></div>} />
      <ChartPane id="pat" symbol={symbol} tf={tf} universe={uni || []} onSymbol={setSymbol} onTf={setTf} height={420} overlays="ema21,ema50" onData={setChart} />
      <Panel title={`Detected patterns (${pats.length})`} className="mt-3" testid="patterns-table">
        {!pats.length ? <Empty>No patterns on this chart</Empty> : (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {pats.map((p) => (
              <div key={p.key} className="rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid={`pattern-card-${p.type.replace(/\s/g, "-").toLowerCase()}-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-display text-sm font-semibold ${p.direction === "bullish" ? "text-emerald-600" : p.direction === "bearish" ? "text-red-600" : "text-slate-800"}`}>{p.type}</span>
                  <Tag v={p.status} />
                </div>
                <div className="mono mt-1 text-[10px] text-slate-500">detected {ts(p.detected_time * 1000)} · heuristic {p.heuristic_score}{p.confirm_level ? ` · confirm ${p.confirm_level}` : ""}{p.invalid_level ? ` · invalid ${p.invalid_level}` : ""}</div>
                <p className="mt-2 text-xs text-slate-700">{lang === "ms" ? p.explanation_ms : p.explanation_en}</p>
                <p className="mt-1 text-[11px] text-slate-500">{lang === "ms" ? p.explanation_en : p.explanation_ms}</p>
                {p.history?.length > 1 && <div className="mono mt-2 text-[10px] text-slate-500">{p.history.map((h) => h.status).join(" → ")}</div>}
                <div className="mt-2"><AIExplain kind="pattern" payload={{ symbol, tf, ...p, lines: undefined }} testid={`pattern-ai-${p.id}`} /></div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
